/*
 * ******************************************************************
 * Copyright (c) 2022-2025 Broadcom. All Rights Reserved.
 * Broadcom Confidential. The term "Broadcom" refers to Broadcom Inc.
 * and/or its subsidiaries.
 * ******************************************************************
 */
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, Output, QueryList, ViewChild, ViewChildren } from "@angular/core";
import { ClrDatagridFilter, ClrDatagridFilterInterface, ClrDateContainer, ClrPopoverEventsService, ClrPopoverToggleService } from "@clr/angular";
import { Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

// @formatter:off
@Component({
    standalone: false,
    selector: 'dg-single-date-filter',
    templateUrl: './datagrid-single-date-filter.component.html',
    styleUrls: ['./datagrid-single-date-filter.component.scss']
})
// @formatter:on
export class DatagridSingleDateFilter<T> implements ClrDatagridFilterInterface<T>, AfterViewInit, OnDestroy {
    @ViewChild('input', { read: ElementRef })
    private inputRef: ElementRef;

    @ViewChildren(ClrDateContainer)
    private dateContainerQuery: QueryList<ClrDateContainer>;

    // The filter should return pure date (with no time at the end) when its state is taken
    @Input()
    public dateOnly: boolean;

    @Input()
    public filterProperty: string;

    @Input()
    public set clrFilterValue(value: Date) {
        this.date = value ? new Date(value) : null;
    }
    public get clrFilterValue(): string {
        return this.date ? new Date(this.date).toISOString() : null;
    }

    @Output()
    clrFilterValueChange: EventEmitter<any> = new EventEmitter();

    private changesSubject: Subject<T> = new Subject<T>();
    private destroySubject: Subject<void> = new Subject<void>();
    // Track the number of open calendar controls - in some opening positions, it's possible to open a second calendar
    // before the first one is closed (the button is not covered by the first one).
    private calendarsOpen: number = 0;

    public date: Date;

    constructor(protected filterContainer: ClrDatagridFilter,
                private clrPopoverEventsService: ClrPopoverEventsService,
                private smartToggleService: ClrPopoverToggleService,
                private ngZone: NgZone) {
        filterContainer.setFilter(this);
    }

    ngAfterViewInit(): void {
        this.dateContainerQuery.map(dateContainer => {
            // toggleService is private, but we need it to track when a date-picker is open or closed
            const toggleService: ClrPopoverToggleService = dateContainer['toggleService'];
            toggleService.openChange
                .pipe(takeUntil(this.destroySubject))
                .subscribe(open => this.switchEventListeners(open));
        });
        this.smartToggleService.openChange
            .pipe(takeUntil(this.destroySubject))
            .subscribe(() => {
                this.ngZone.runOutsideAngular(() => {
                    requestAnimationFrame(() => {
                        this.inputRef.nativeElement.focus();
                    });
                });
            });
    }

    switchEventListeners(open: boolean): void {
        if (open) {
            if (this.calendarsOpen === 1) {
                // the second calendar is open before the first is closed
                this.calendarsOpen++;
            } else {
                this.calendarsOpen++;
                // Stop listening to filter closing events until the date-picker is closed.
                setTimeout(() => {
                    this.clrPopoverEventsService.removeClickListener();
                    this.clrPopoverEventsService.removeEscapeListener();
                    this.clrPopoverEventsService.removeScrollListener();
                });
            }
        } else {
            if (this.calendarsOpen > 1) {
                // the second calendar is open before the first is closed
                this.calendarsOpen--;
            } else {
                this.calendarsOpen--;
                // The date-picker is closed, so restart all listeners.
                setTimeout(() => {
                    this.clrPopoverEventsService.addClickListener();
                    this.clrPopoverEventsService.addEscapeListener();
                    this.clrPopoverEventsService.addScrollListener();
                });
            }
        }
    }

    onDateChange(date: Date): void {
        this.date = date ? new Date(date) : null;
        this.changesSubject.next(null);
        this.clrFilterValueChange.emit(this.date);
    }

    /**
     * Indicates if the filter is currently active, (at least one input is set)
     */
    public isActive(): boolean {
        return (this.date != null);
    }

    /**
     * The method is not used for server-driven datagrids, so no need to implement it.
     */
    public accepts(_item: T): boolean {
        return false;
    }

    /**
     * The Observable required as part of the Filter interface
     */
    public get changes(): Observable<T> {
        return this.changesSubject.asObservable();
    }

    public get state(): any {
        const isoDate = new Date(this.date).toISOString();
        return {
            property: this.filterProperty,
            value: this.date
                ?  this.dateOnly ? [isoDate.slice(0,10)] : [isoDate]
                : null
        }
    }

    public ngOnDestroy(): void {
        this.destroySubject.next();
        this.destroySubject.complete();
    }

}
